import { z } from 'zod';

/**
 * Traveller documents (post-contract PAN / passport collection).
 * Server source: apps/api/src/modules/traveller-documents.
 *
 * Shared by the admin Documents tab (full view, S3-backed downloads) and the
 * customer upload page on the website (public view keyed by the opaque token,
 * never exposes storage keys).
 */

export const TRAVELLER_DOCUMENT_REQUEST_STATUSES = [
  'PENDING',
  'PARTIAL',
  'SUBMITTED',
  'VERIFIED',
  'CANCELLED',
] as const;
export const TravellerDocumentRequestStatusSchema = z.enum(TRAVELLER_DOCUMENT_REQUEST_STATUSES);
export type TravellerDocumentRequestStatus = z.infer<typeof TravellerDocumentRequestStatusSchema>;

export const TRAVELLER_DOCUMENT_TRIP_SCOPES = ['DOMESTIC', 'INTERNATIONAL'] as const;
export const TravellerDocumentTripScopeSchema = z.enum(TRAVELLER_DOCUMENT_TRIP_SCOPES);
export type TravellerDocumentTripScope = z.infer<typeof TravellerDocumentTripScopeSchema>;

export const TRAVELLER_DOCUMENT_TYPES = ['PAN', 'PASSPORT'] as const;
export const TravellerDocumentTypeSchema = z.enum(TRAVELLER_DOCUMENT_TYPES);
export type TravellerDocumentType = z.infer<typeof TravellerDocumentTypeSchema>;

/** Accepted upload types and the per-file ceiling (raw bytes, pre-base64). */
export const TRAVELLER_DOCUMENT_ACCEPTED_MIME = [
  'application/pdf',
  'image/jpeg',
  'image/png',
  'image/webp',
] as const;
export const TRAVELLER_DOCUMENT_MAX_BYTES = 8 * 1024 * 1024;

/** What the customer must upload, derived from scope + traveller count. */
export const TravellerDocumentRequirementsSchema = z.object({
  pan: z.number().int().min(0),
  passports: z.number().int().min(0),
});
export type TravellerDocumentRequirements = z.infer<typeof TravellerDocumentRequirementsSchema>;

/** A file as the customer sees it on the public page - no storage key. */
export const TravellerDocumentPublicFileSchema = z.object({
  id: z.string().uuid(),
  type: TravellerDocumentTypeSchema,
  travellerName: z.string().nullable(),
  fileName: z.string(),
  mimeType: z.string(),
  sizeBytes: z.number().int(),
  uploadedAt: z.coerce.date(),
  rejectedAt: z.coerce.date().nullable(),
  rejectReason: z.string().nullable(),
});
export type TravellerDocumentPublicFile = z.infer<typeof TravellerDocumentPublicFileSchema>;

export const TravellerDocumentPublicViewSchema = z.object({
  status: TravellerDocumentRequestStatusSchema,
  tripScope: TravellerDocumentTripScopeSchema,
  travellerCount: z.number().int(),
  tripTitle: z.string(),
  customerFirstName: z.string(),
  requirements: TravellerDocumentRequirementsSchema,
  files: z.array(TravellerDocumentPublicFileSchema),
  /** True once the required set is complete (SUBMITTED / VERIFIED). */
  complete: z.boolean(),
  /** False once VERIFIED or CANCELLED - the page goes read-only. */
  editable: z.boolean(),
  submittedAt: z.coerce.date().nullable(),
});
export type TravellerDocumentPublicView = z.infer<typeof TravellerDocumentPublicViewSchema>;

export const TravellerDocumentUploadInputSchema = z.object({
  type: TravellerDocumentTypeSchema,
  travellerName: z.string().trim().max(120).optional(),
  fileName: z.string().trim().min(1).max(200),
  /** Bare base64 (no data: prefix). */
  contentBase64: z.string().min(1),
});
export type TravellerDocumentUploadInput = z.infer<typeof TravellerDocumentUploadInputSchema>;

/** Admin view of a file - adds the storage key so staff can fetch a download URL. */
export const TravellerDocumentFileSchema = TravellerDocumentPublicFileSchema.extend({
  uploadedIp: z.string().nullable(),
});
export type TravellerDocumentFile = z.infer<typeof TravellerDocumentFileSchema>;

export const TravellerDocumentRequestSchema = z.object({
  id: z.string().uuid(),
  token: z.string(),
  status: TravellerDocumentRequestStatusSchema,
  tripScope: TravellerDocumentTripScopeSchema,
  travellerCount: z.number().int(),
  tripTitle: z.string(),
  contractId: z.string().uuid().nullable(),
  itineraryId: z.string().uuid().nullable(),
  leadId: z.string().uuid().nullable(),
  customerId: z.string().uuid(),
  customerName: z.string(),
  customerEmail: z.string().nullable(),
  customerMobile: z.string().nullable(),
  requestedAt: z.coerce.date(),
  submittedAt: z.coerce.date().nullable(),
  verifiedAt: z.coerce.date().nullable(),
  verifiedById: z.string().uuid().nullable(),
  cancelledAt: z.coerce.date().nullable(),
  lastReminderAt: z.coerce.date().nullable(),
  reminderCount: z.number().int(),
  staffNote: z.string().nullable(),
  createdAt: z.coerce.date(),
  /** Customer-facing upload URL (WEBSITE_URL + /documents/<token>). */
  uploadUrl: z.string(),
  requirements: TravellerDocumentRequirementsSchema,
  /**
   * One-line "what we need" as the customer messages word it (e.g. "Passport
   * of all 3 travellers and PAN card of any one traveller") - the WhatsApp
   * widget prefills the documents templates' {{2}} with it.
   */
  requiredSummary: z.string().default(''),
  files: z.array(TravellerDocumentFileSchema).default([]),
});
export type TravellerDocumentRequest = z.infer<typeof TravellerDocumentRequestSchema>;

export const TravellerDocumentRequestListSchema = z.array(TravellerDocumentRequestSchema);

export const TravellerDocumentDownloadSchema = z.object({
  url: z.string(),
  expiresAt: z.coerce.date(),
});
export type TravellerDocumentDownload = z.infer<typeof TravellerDocumentDownloadSchema>;

export const CreateTravellerDocumentRequestInputSchema = z.object({
  leadId: z.string().uuid(),
  tripScope: TravellerDocumentTripScopeSchema.optional(),
  travellerCount: z.number().int().min(1).max(60).optional(),
});
export type CreateTravellerDocumentRequestInput = z.infer<
  typeof CreateTravellerDocumentRequestInputSchema
>;

/** Pure helper shared by API + both UIs: what a request needs. */
export function travellerDocumentRequirements(
  tripScope: TravellerDocumentTripScope,
  travellerCount: number,
): TravellerDocumentRequirements {
  return {
    pan: 1,
    passports: tripScope === 'INTERNATIONAL' ? Math.max(1, travellerCount) : 0,
  };
}
